import { IconButton, type SxProps, type Theme, useTheme } from "@mui/material";
import { MicIcon } from "components/icons";
import { Tooltip } from "components/tooltip";

const isMacOS =
  typeof navigator !== "undefined" &&
  /Mac|iPod|iPhone|iPad/.test(navigator.platform);
const pushToTalkHint = (
  <>
    Dictate
    <br />
    {`Press & hold “${isMacOS ? "option" : "alt"}” key`}
  </>
);

/**
 * The mic as one of a rich-text toolbar's own buttons (32px, radius 6, the
 * toolbar icon colour). `endGap` is the toolbar's side padding, so the mic
 * keeps the same inset from the edge as the toolbar's first button.
 */
export const toolbarMicSx =
  (endGap: string): SxProps<Theme> =>
  (theme) => ({
    mr: endGap,
    width: "32px",
    height: "32px",
    borderRadius: "6px",
    color: theme.palette.lightBg.medium,
    "&:hover": { backgroundColor: theme.palette.neutral["010"] },
  });

interface DictationMicButtonProps {
  onClick: () => void;
  /** Size and placement — each composer's button row differs. */
  sx?: SxProps<Theme>;
}

/** The mic that starts a dictation, with the push-to-talk hint. */
export const DictationMicButton = ({
  onClick,
  sx,
}: DictationMicButtonProps) => {
  const theme = useTheme();

  return (
    <Tooltip title={pushToTalkHint} disableSentenceCase>
      <IconButton
        aria-label="Dictate a message"
        aria-keyshortcuts="Alt"
        onClick={onClick}
        sx={[
          { color: theme.palette.lightBg.low },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <MicIcon sx={{ fontSize: "20px" }} />
      </IconButton>
    </Tooltip>
  );
};
